import React, { useMemo } from "react";
import {
  Text,
  StyleSheet,
  View,
  ImageBackground,
  TouchableOpacity,
} from "react-native";
import { MaterialIcons } from "@expo/vector-icons";
import { useThemeSettings } from "../ThemeProvider";
import { GradientStrokeBox } from "./GradientStrokeBox";
import { CustomIcon } from "./CustomIcon";
import { getImageUri, isSingleWord } from "../shared/helpers/utils";
import { TrackedTouchable } from "../analytics/components";
import { useAudioContext } from "../providers/AudioProvider";
import getGlobalStyles from "../globalStyles";
import { FULL_WIDTH } from "../shared/constants/dimension";

interface CollectionBubbleProps {
  collection?: any;
  index: number;
  showTopRightIcon?: boolean;
  showPlayOnListen?: boolean;
  showEdit?: boolean;
  isDisabled?: boolean;
  isSelected?: boolean;
  onTopRightPress?: (collectionId: string, includeThinko?: boolean) => void;
  onBubblePress?: (collection: any) => void;
  onEditItem?: (collection: any) => void;
  onDeleteItem?: (collection: any) => void;
  showFollowTour?: boolean;
}
export const CollectionBubble = ({
  collection,
  index,
  onTopRightPress,
  onBubblePress,
  onEditItem,
  onDeleteItem,
  showPlayOnListen,
  showEdit,
  isSelected,
  showTopRightIcon,
  isDisabled,
  showFollowTour = false,
}: CollectionBubbleProps) => {
  const { theme } = useThemeSettings();
  const { isPlaying } = useAudioContext();
  const gStyles = useMemo(() => getGlobalStyles(theme), [theme]);
  const itemOffsetsRef = React.useRef<{ [key: string]: number }>({});

  const getTopRightIcon = () => {
    const style = collection.is_thinko ? {
      top: 3,
      right: 3,
      width: 20,
      height: 20,
      borderRadius: 10,
      backgroundColor: theme.colors.appBackground3,
    } : {
      top: 2,
      right: 15,
      width: 20,
      height: 20,
      borderRadius: 10,
      backgroundColor: 'transparent',
    };
    const iconContent = (
      <View
        style={{
          borderColor: "transparent",
          alignItems: "center",
          paddingVertical: 3,
        }}
      >
        {collection.is_public && <CustomIcon icon={"trending"} size={13} />}
      </View>
    );

    const icon = (
      <View
        style={{
          position: "absolute",
          ...style,
          zIndex: 10,
        }}
      >
        {iconContent}
      </View>
    );
    if (collection.is_thinko) {
      const followButton = (
        <TrackedTouchable
          context={{ cta: "ADD_RECOMMENDATION" }}
          id={showFollowTour ? "followCollectionIcon" : `addRecommendation${index}`}
          onPress={() => onTopRightPress?.(collection.id, true)}
        >
          <GradientStrokeBox
            wrapperStyle={{
              width: 26,
              height: 26,
              borderRadius: 13,
            }}
          >
            {icon}
          </GradientStrokeBox>
        </TrackedTouchable>
      );

      return (
        <View
          style={{
            position: "absolute",
            top: 2,
            right: 10,
            zIndex: 10,
          }}
        >
          {followButton}
        </View>
      );
    } else {
      return icon;
    }
  }
  const getInnerBubble = () => {
    return <ImageBackground
      source={(collection?.is_thinko && collection.title === "For You") ? require("../assets/images/for_you.png") : getImageUri(collection?.firstEventImage!)}
      style={styles.selectedImage}
      imageStyle={{ borderRadius: 35 }}
    >
      {showEdit && getEditControls()}
      {showPlayOnListen && (
        <TrackedTouchable
          context={{
            cta:
              isSelected && isPlaying
                ? "AUDIO_PAUSE"
                : "AUDIO_PLAY",
          }}
          style={[gStyles.buttonStyle, gStyles.buttonCircle]}
          onPress={(e: any) => {
            e?.stopPropagation?.();
            onBubblePress?.(collection);
          }}
          onLayout={(e) => {
            itemOffsetsRef.current[collection.id] = e.nativeEvent.layout.x;
          }}
        >
          <CustomIcon
            size={16}
            icon={
              isSelected && isPlaying
                ? "pause"
                : "play"
            }
            color={theme?.colors?.primary}
          />
        </TrackedTouchable>
      )}
    </ImageBackground>
  }
  const getEditControls = () => {
    return <View style={[styles.iconOverlay, {borderColor: theme.colors.primary, backgroundColor: theme.colors.appBackground3}]}>
      <TouchableOpacity onPress={()=>onEditItem?.(collection)} style={styles.iconButton}>
        <MaterialIcons
          name="edit"
          size={14}
          color={theme?.dark ? theme?.colors?.white : theme?.colors?.primary}
        />
      </TouchableOpacity>
      <TouchableOpacity onPress={()=>onDeleteItem?.(collection)} style={styles.iconButton}>
        <MaterialIcons
          name="delete"
          size={14}
           color={theme?.dark ? theme?.colors?.white : theme?.colors?.primary}
        />
      </TouchableOpacity>
    </View>
  }
  return (
    <View style={isDisabled && { opacity: 0.4 }}>
      {showTopRightIcon && getTopRightIcon()}
      <TrackedTouchable
        context={{ cta: "SWITCH_COLLECTIONS", collectionId: collection.id }}
        style={styles.item}
        onPress={() => onBubblePress?.(collection)}
        onLayout={(e) => {
          itemOffsetsRef.current[collection.id] = e.nativeEvent.layout.x;
        }}
      >
        {isSelected && showTopRightIcon? (
          <GradientStrokeBox wrapperStyle={{ width: 70, height: 70, borderRadius: 35 }}>
            {getInnerBubble()}
          </GradientStrokeBox>
        ) : (
          <View style={gStyles.bubbleBorder} >
            {getInnerBubble()}
          </View>
        )}
        <Text
          numberOfLines={isSingleWord(collection.title) ? 1 : 2}
          ellipsizeMode="tail"
          style={[
            styles.label,
            isSelected && styles.activeLabel,
            { color: isSelected ? theme?.colors?.primary : theme?.colors?.headerText },
          ]}
        >
          {collection.title}
        </Text>
      </TrackedTouchable>
    </View>
  );
}

const styles = StyleSheet.create({

  item: {
    marginRight: 10,
    alignItems: "center",
  },

  label: {
    marginTop: 6,
    fontSize: 12,
    fontWeight: "500",
    width: 70,
    textAlign: "center",
  },

  activeLabel: {
    fontWeight: "800",
  },
  selectedImage: {
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
  },

  iconOverlay: {
    flexDirection: "row",
    borderRadius: 20,
    paddingHorizontal: 6,
    paddingVertical: 4,
    marginHorizontal:10,
    width:FULL_WIDTH,
    borderWidth: 1,
    gap: 8,
  },

  iconButton: {
    padding: 4,
  },
});
